import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { splitBoards } from "@/lib/board/open-board";
import type { Board, Employee, Team } from "@/types/db";
import { fmtDate, initials } from "@/lib/utils";
import { X } from "lucide-react";

export function TasksHome() {
  const { employee } = useAuth();
  const [boards, setBoards] = useState<Board[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [people, setPeople] = useState<Employee[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [boardType, setBoardType] = useState<"personal" | "team">("personal");
  const [teamId, setTeamId] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    const [{ data: b }, { data: t }, { data: p }] = await Promise.all([
      supabase.from("hq_task_boards").select("*").eq("archived", false).order("created_at", { ascending: false }),
      supabase.from("hq_teams").select("*").order("name"),
      supabase.from("hq_employees").select("*").eq("status", "active").order("first_name"),
    ]);
    setBoards((b as Board[]) ?? []);
    setTeams((t as Team[]) ?? []);
    setPeople((p as Employee[]) ?? []);
  }

  const peopleById = useMemo(() => Object.fromEntries(people.map(p => [p.id, p])), [people]);
  const memberCandidates = useMemo(() => {
    const q = memberSearch.toLowerCase();
    return people.filter(p => {
      if (p.id === employee?.id) return false;            // owner is implicitly a member
      if (memberIds.includes(p.id)) return false;
      if (!q) return true;
      const hay = `${p.first_name ?? ""} ${p.last_name ?? ""} ${p.email}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 8);
  }, [people, memberSearch, memberIds, employee?.id]);

  async function createBoard(e: React.FormEvent) {
    e.preventDefault();
    if (!employee) return;
    setBusy(true); setError(null);
    const payload: Record<string, unknown> = {
      name,
      description: desc || null,
      created_by: employee.id,
    };
    if (boardType === "personal") payload.owner_employee_id = employee.id;
    else { payload.team_id = teamId; }
    const { data: created, error: insErr } = await supabase
      .from("hq_task_boards")
      .insert(payload)
      .select("id")
      .single();
    if (insErr || !created) {
      setBusy(false);
      setError(insErr?.message ?? "Could not create board");
      return;
    }
    // Attach explicit board members (independent of team membership).
    if (memberIds.length > 0) {
      const rows = memberIds.map(eid => ({
        board_id: (created as { id: string }).id,
        employee_id: eid,
        added_by: employee.id,
      }));
      const { error: memErr } = await supabase.from("hq_board_members").insert(rows);
      if (memErr) {
        setBusy(false);
        setError("Board created, but adding members failed: " + memErr.message);
        return;
      }
    }
    setBusy(false);
    setShowNew(false); setName(""); setDesc(""); setTeamId(""); setMemberIds([]); setMemberSearch("");
    await load();
  }

  const grouped = splitBoards(boards);
  const teamName = (id: string) => teams.find(t => t.id === id)?.name || "Team";

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Boards</h1>
        <button onClick={() => setShowNew(s => !s)} className="btn">+ New Board</button>
      </div>

      {showNew && (
        <div className="card">
          {error && <div className="mb-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
          <form onSubmit={createBoard} className="space-y-4">
            <div><label className="label">Name</label><input className="input" required value={name} onChange={e => setName(e.target.value)} autoFocus /></div>
            <div><label className="label">Description</label><input className="input" value={desc} onChange={e => setDesc(e.target.value)} /></div>
            <div className="flex gap-3 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={boardType === "personal"} onChange={() => setBoardType("personal")} /> Personal (just me)
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={boardType === "team"} onChange={() => setBoardType("team")} /> Team
              </label>
            </div>
            {boardType === "team" && (
              <div>
                <label className="label">Team</label>
                <select required className="input" value={teamId} onChange={e => setTeamId(e.target.value)}>
                  <option value="">— Select team —</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {teams.length === 0 && <p className="mt-1 text-xs text-gray2">No teams yet. Ask admin to create one.</p>}
              </div>
            )}
            <div>
              <label className="label">Additional members <span className="font-normal text-gray2 normal-case">(optional — grants direct access regardless of team)</span></label>
              {memberIds.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {memberIds.map(id => {
                    const p = peopleById[id];
                    if (!p) return null;
                    return (
                      <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-teal-mint pl-1 pr-2 py-0.5 text-xs font-semibold text-teal-dark">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-[10px]">
                          {initials(p.first_name, p.last_name, p.email)}
                        </span>
                        {[p.first_name, p.last_name].filter(Boolean).join(" ") || p.email}
                        <button type="button" className="text-teal-dark hover:text-bad" onClick={() => setMemberIds(ids => ids.filter(x => x !== id))}>
                          <X size={12} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <input
                className="input"
                placeholder="Search teammate by name or email…"
                value={memberSearch}
                onChange={e => setMemberSearch(e.target.value)}
              />
              {memberSearch && memberCandidates.length > 0 && (
                <div className="mt-1 max-h-48 overflow-auto rounded-lg ring-1 ring-line bg-white">
                  {memberCandidates.map(p => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => { setMemberIds(ids => [...ids, p.id]); setMemberSearch(""); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-offwhite"
                    >
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-mint text-[10px] font-bold text-teal-dark">
                        {initials(p.first_name, p.last_name, p.email)}
                      </span>
                      <span>{[p.first_name, p.last_name].filter(Boolean).join(" ") || p.email}</span>
                      <span className="ml-auto text-xs text-gray2">{p.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
              <button className="btn" disabled={busy || !name}>{busy ? "Creating…" : "Create board"}</button>
            </div>
          </form>
        </div>
      )}

      <Section title="Open to all HQ staff" boards={grouped.open} subtitle={() => "Every active employee"} />
      <Section title="My personal boards" boards={grouped.personal} subtitle={p => `Created ${fmtDate(p.created_at).split(",")[0]}`} />
      <Section title="Team boards" boards={grouped.team} subtitle={p => p.team_id ? teamName(p.team_id) : ""} />

      {boards.length === 0 && (
        <div className="card py-12 text-center">
          <h2 className="mb-2 text-lg font-semibold">No boards yet</h2>
          <p className="mb-4 text-sm text-gray2">Create your first personal board to start tracking tasks.</p>
          <button onClick={() => setShowNew(true)} className="btn">+ Create a board</button>
        </div>
      )}
    </div>
  );
}

function Section({ title, boards, subtitle }: { title: string; boards: Board[]; subtitle: (b: Board) => string }) {
  if (boards.length === 0) return null;
  return (
    <div>
      <h2 className="mb-3 text-base font-semibold uppercase tracking-wider text-gray2">{title}</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {boards.map(b => (
          <Link
            key={b.id}
            to={`/tasks/${b.id}`}
            className="card transition hover:-translate-y-0.5 hover:shadow-soft hover:ring-teal"
          >
            <h3 className="text-lg font-semibold">{b.name}</h3>
            {b.description && <p className="mt-1 text-sm text-gray2 line-clamp-2">{b.description}</p>}
            <div className="mt-3 text-xs text-gray2">{subtitle(b)}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
